/**
 * Sign-in state and tokens.
 *
 * - The access token is kept in memory only and attached by the API client.
 * - The refresh token is kept in the TokenStore (see tokenStore.ts) and
 *   swapped for a new pair on every refresh (the server rotates it).
 * - Concurrent refreshes are merged into one request, and serialized across
 *   browser tabs with the Web Locks API where available, so two tabs never
 *   present the same refresh token (the server would treat that as theft).
 */
import { API_BASE } from "./config";
import { ApiError, errorFrom } from "./errors";
import { getTokenStore, memoryStore, setTokenStore } from "./tokenStore";
import type { Schema } from "./types";
import { isViewLaunch, readView, writeView, type StoredView } from "./viewStore";

export type User = Schema<"UserOut">;
type TokenOut = Schema<"TokenOut">;

export type SessionStatus = "loading" | "signed-in" | "signed-out";
export interface SessionState {
  status: SessionStatus;
  user: User | null;
}

const EARLY_REFRESH_MS = 30_000; // refresh a little before the access token expires
const LOCK_NAME = "rai-refresh";

let access: { token: string; expiresAt: number } | null = null;
let state: SessionState = { status: "loading", user: null };
const listeners = new Set<() => void>();
let inflight: Promise<boolean> | null = null;

/**
 * "View as this user" (V044, viewStore.ts): this tab shows another user's app
 * read-only. Its token comes from sessionStorage, never the admin's stored
 * sign-in: a view tab uses an in-memory token store from the start, so it can
 * neither read nor overwrite the admin's refresh token.
 */
export interface ViewSession {
  viewerName: string;
  expiresAt: number;
  label?: string;
}
let view: ViewSession | null = null;
let viewTab = false;

function startViewTab(): void {
  viewTab = true;
  setTokenStore(memoryStore());
  const stored = readView<User>();
  if (stored && stored.expiresAt > Date.now()) {
    view = { viewerName: stored.viewerName, expiresAt: stored.expiresAt, label: stored.label };
    access = { token: stored.token, expiresAt: stored.expiresAt };
    state = { status: "signed-in", user: stored.user };
  } else if (stored) {
    writeView(null);
  }
}

if (isViewLaunch()) startViewTab();

/** This tab is an admin's view of a user (now, or it was). */
export function isViewTab(): boolean {
  return viewTab;
}

/** The live view session, or null. */
export function getView(): ViewSession | null {
  return view;
}

function setState(next: SessionState): void {
  state = next;
  listeners.forEach((fn) => fn());
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getSession(): SessionState {
  return state;
}

/** A usable access token, or null when there is none or it is about to expire. */
export function getAccessToken(): string | null {
  if (view && access) return access.expiresAt > Date.now() ? access.token : null;
  if (!access || access.expiresAt - Date.now() < EARLY_REFRESH_MS) return null;
  return access.token;
}

export function hasRefreshToken(): boolean {
  return getTokenStore().getRefresh() !== null;
}

function apply(tokens: TokenOut): void {
  access = { token: tokens.access_token, expiresAt: Date.parse(tokens.access_expires_at) };
  getTokenStore().setRefresh(tokens.refresh_token);
  setState({ status: "signed-in", user: tokens.user });
}

/** Start a session from a token pair another endpoint returned (accepting an
 * RA's invitation signs the new client in, V035). */
export function adoptSession(tokens: TokenOut): User {
  apply(tokens);
  return tokens.user;
}

/** Forget the session in this tab (no server call). */
export function clearSession(): void {
  access = null;
  getTokenStore().setRefresh(null);
  setState({ status: "signed-out", user: null });
}

async function post(path: string, body: unknown): Promise<Response> {
  try {
    return await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, null);
  }
}

export async function login(email: string, password: string): Promise<User> {
  const res = await post("/auth/login", { email, password });
  if (!res.ok) throw await errorFrom(res);
  const tokens = (await res.json()) as TokenOut;
  apply(tokens);
  return tokens.user;
}

/** Set by a deliberate sign-out, so the app goes to the home page rather than to
 * sign-in (which is where an expired session goes). Read once. */
let signedOutOnPurpose = false;

export function consumeSignOutIntent(): boolean {
  const was = signedOutOnPurpose;
  signedOutOnPurpose = false;
  return was;
}

/**
 * Work that needs the signed-in user's token just before a deliberate sign-out (the
 * app forgets this phone for push notifications, src/native/push.ts). Each step gets
 * a few seconds at most, so signing out never hangs on a slow network.
 */
const beforeSignOut = new Set<() => Promise<void>>();
const BEFORE_SIGN_OUT_MS = 3000;

export function onBeforeSignOut(fn: () => Promise<void>): () => void {
  beforeSignOut.add(fn);
  return () => beforeSignOut.delete(fn);
}

async function runBeforeSignOut(): Promise<void> {
  if (beforeSignOut.size === 0) return;
  const limit = new Promise<void>((resolve) => setTimeout(resolve, BEFORE_SIGN_OUT_MS));
  const steps = [...beforeSignOut].map((fn) => fn().catch(() => undefined));
  await Promise.race([Promise.all(steps), limit]);
}

export async function logout(everywhere = false): Promise<void> {
  if (viewTab) return endView();
  await runBeforeSignOut();
  const refresh = getTokenStore().getRefresh();
  signedOutOnPurpose = true;
  clearSession();
  if (refresh) {
    try {
      await post("/auth/logout", { refresh_token: refresh, everywhere });
    } catch {
      // offline: the token expires on its own; it's already gone locally
    }
  }
}

async function doRefresh(): Promise<boolean> {
  const refresh = getTokenStore().getRefresh();
  if (!refresh) {
    clearSession();
    return false;
  }
  const res = await post("/auth/refresh", { refresh_token: refresh });
  if (res.ok) {
    apply((await res.json()) as TokenOut);
    return true;
  }
  // 403: the subscription ended (paid plans); signing in again shows how to renew
  if (res.status === 401 || res.status === 403 || res.status === 422) {
    clearSession();
    return false;
  }
  throw await errorFrom(res); // server trouble: keep the session, let the caller show it
}

/**
 * Get a fresh access token using the refresh token. Resolves true on success,
 * false when the user must sign in again. Concurrent calls share one request.
 */
export function refreshAccess(): Promise<boolean> {
  if (viewTab) {
    // a view has no refresh token: when its hour is up, it is over
    endViewLocally();
    return Promise.resolve(false);
  }
  if (inflight) return inflight;
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  const run: Promise<boolean> = locks
    ? // the lock resolves with the callback's promise; then() unwraps it for the types
      locks.request(LOCK_NAME, () => doRefresh()).then((ok) => ok)
    : doRefresh();
  const shared = run.finally(() => {
    inflight = null;
  });
  inflight = shared;
  return shared;
}

/** On app start: resume the previous session if a refresh token is stored. */
export async function bootstrap(): Promise<SessionState> {
  if (viewTab) {
    if (!getAccessToken()) endViewLocally();
    return state;
  }
  if (state.status === "signed-in" && getAccessToken()) return state;
  if (!hasRefreshToken()) {
    setState({ status: "signed-out", user: null });
    return state;
  }
  try {
    await refreshAccess();
  } catch {
    // API unreachable: show the sign-in page rather than a blank screen
    setState({ status: "signed-out", user: null });
  }
  return state;
}

// Another tab signed out (removed the stored token): follow it.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (viewTab) return; // the admin signing out elsewhere doesn't change what this tab views
    if (e.key === "rai.refresh" && e.newValue === null && state.status === "signed-in") {
      access = null;
      setState({ status: "signed-out", user: null });
    }
  });
}

type ViewTokenOut = Schema<"ViewTokenOut">;

/** Swap the one-time code from Admin → Users for a view session in this tab. */
export async function startView(code: string): Promise<User> {
  if (!viewTab) startViewTab();
  const res = await post("/auth/view-session/exchange", { code });
  if (!res.ok) throw await errorFrom(res);
  const out = (await res.json()) as ViewTokenOut;
  const expiresAt = Date.parse(out.access_expires_at);
  const stored: StoredView<User> = {
    token: out.access_token,
    expiresAt,
    viewerName: out.viewer_name,
    label: out.view_label,
    user: out.user,
  };
  writeView(stored);
  view = { viewerName: out.viewer_name, expiresAt, label: out.view_label };
  access = { token: out.access_token, expiresAt };
  setState({ status: "signed-in", user: out.user });
  return out.user;
}

function endViewLocally(): void {
  writeView(null);
  view = null;
  access = null;
  if (state.status !== "signed-out") setState({ status: "signed-out", user: null });
}

/** "Close view": end the view on the server (its token stops working) and here. */
export async function endView(): Promise<void> {
  const token = access?.token;
  endViewLocally();
  if (!token) return;
  try {
    await fetch(`${API_BASE}/auth/view-session/end`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // offline: the view ends on its own within the hour, and is gone here
  }
}

/** For tests: reset module state. */
export function resetSessionForTests(next: SessionState = { status: "loading", user: null }): void {
  access = null;
  inflight = null;
  state = next;
  view = null;
  viewTab = false;
}

/** For tests: run the start-up check again (after setting sessionStorage or the URL). */
export function initViewTabForTests(): void {
  if (isViewLaunch()) startViewTab();
}

/** For tests and stories: pretend to be signed in. */
export function setSessionForTests(user: User, token = "test-access-token"): void {
  access = { token, expiresAt: Date.now() + 15 * 60_000 };
  setState({ status: "signed-in", user });
}
